import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HeartHandshake, Calendar, History, CreditCard, User, LogOut,
  Plus, Clock, MapPin, ArrowRight, Sparkles, Search, Star, X, 
  Repeat, Pause, Play, XCircle, Gift, Award, TrendingUp, MessageCircle, Shield,
  Share2, Copy, Users, Check, MessageSquare, Heart, Image, Download, RefreshCw, FileText, AlertTriangle, FileSignature,
  Shirt, Truck, Wrench, Upload, Bell, ChevronRight, CheckCircle, Car, Eye, Navigation, Phone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, bookingsAPI, reviewsAPI, recurringAPI, loyaltyAPI, messagingAPI, referralsAPI, customerAPI, paymentsAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ChatModal } from '../components/Chat';
import ChatInterface from '../components/ChatInterface';
import NotificationBell from '../components/NotificationBell';
import UniversalUploader from '../components/common/UniversalUploader';
import { 
  FavoriteWorkersTab, 
  SavedAddressesTab, 
  FamilyMembersTab, 
  PaymentMethodsTab, 
  ServiceGalleryTab,
  UpcomingAppointmentCard 
} from '../components/customer';
import CustomRequestsTab from '../components/customer/CustomRequestsTab';
import TipWorker from '../components/customer/TipWorker';
import ServiceRequestsTab from '../components/customer/ServiceRequestsTab';
import RideBookingTab from '../components/customer/RideBookingTab';
import IncidentReportSection from '../components/shared/IncidentReportSection';
import CustomerPoliciesTab from '../components/customer/CustomerPoliciesTab';
import SupportButton from '../components/common/SupportButton';
import VideoCallModal from '../components/common/VideoCallModal';
import IncomingCallModal from '../components/common/IncomingCallModal';
import ContactsList from '../components/common/ContactsList';
import BottomNavBar from '../components/common/BottomNavBar';
import useRideNotifications from '../hooks/useRideNotifications';
import NotificationPrompt from '../components/common/NotificationPrompt';
import { MobileHomeDashboard, MobileBookingWizard } from '../components/mobile';

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const CustomerDashboard = ({ previewMode = false }) => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // In preview mode, use a sample user
  const user = previewMode ? {
    id: 'preview-user',
    full_name: 'Sample Customer',
    email: 'preview@example.com',
    phone: '(919) 555-0000',
    role: 'customer'
  } : authUser;
  
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedRideForTracking, setSelectedRideForTracking] = useState(null);
  const [cancellingRideId, setCancellingRideId] = useState(null);
  const [showRideCancelModal, setShowRideCancelModal] = useState(false);
  const [rideToCancel, setRideToCancel] = useState(null);
  
  // Mobile booking wizard state
  const [showMobileBooking, setShowMobileBooking] = useState(false);
  const [selectedServiceForMobile, setSelectedServiceForMobile] = useState(null);
  
  // Video/Voice Call state
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  
  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [bookingReviews, setBookingReviews] = useState({});
  
  // Recurring bookings state
  const [recurringSeries, setRecurringSeries] = useState([]);
  const [managingRecurring, setManagingRecurring] = useState(null);
  
  // Loyalty program state
  const [loyaltyAccount, setLoyaltyAccount] = useState(null);
  const [loyaltyVouchers, setLoyaltyVouchers] = useState([]);
  const [redeemingReward, setRedeemingReward] = useState(null);
  
  // Referral state
  const [referralCode, setReferralCode] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [myReferrals, setMyReferrals] = useState([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // Feedback state
  const [bookingsToReview, setBookingsToReview] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({ booking_id: '', rating: 5, comment: '' });
  // Note: submittingReview is declared earlier in the file
  
  // Messaging state
  const [showChat, setShowChat] = useState(false);
  const [chatType, setChatType] = useState('support');
  const [chatBookingId, setChatBookingId] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [conversations, setConversations] = useState([]);
  
  // Notification prompt state
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // Enable ride notifications with real-time polling
  const { activeRides: notificationActiveRides, refreshRides } = useRideNotifications(
    user?.id,
    'customer',
    !previewMode && !!user?.id
  );
  
  // Check if we should show notification prompt
  useEffect(() => {
    if (!previewMode && user?.id) {
      const notificationsDismissed = localStorage.getItem('notificationPromptDismissed');
      if (!notificationsDismissed && 'Notification' in window && Notification.permission === 'default') {
        // Show prompt after a short delay
        const timer = setTimeout(() => setShowNotificationPrompt(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [previewMode, user?.id]);

  // Start a call handler
  const handleStartCall = async (contact, callType = 'video') => {
    try {
      const response = await api.post('/calls/initiate', {
        recipient_id: contact.id || contact.user_id,
        call_type: callType
      });
      
      setActiveCall({
        callId: response.data.call_id,
        callType: callType,
        direction: 'outgoing',
        otherParty: {
          id: contact.id || contact.user_id,
          name: contact.full_name || contact.user_name,
          role: contact.role || contact.user_role
        }
      });
      setShowCallModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start call');
    }
  };

  // Handle start chat
  const handleStartChat = (contact) => {
    setActiveTab('messages');
    toast.info(`Opening chat with ${contact.full_name || contact.user_name}`);
  };

  // Quick Rebook modal state
  const [showRebookModal, setShowRebookModal] = useState(false);
  const [rebookingBooking, setRebookingBooking] = useState(null);
  const [rebookDate, setRebookDate] = useState('');

  // Tip worker state
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipBooking, setTipBooking] = useState(null);
  const [bookingTips, setBookingTips] = useState({});
  const [rebookTime, setRebookTime] = useState('');
  const [rebookSameWorker, setRebookSameWorker] = useState(true);
  const [rebooking, setRebooking] = useState(false);

  // Service search state
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [filteredServices, setFilteredServices] = useState([]);

  // Cancel booking state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Services are public - always fetch them
      const servicesRes = await servicesAPI.getAll();
      setServices(servicesRes.data);
      
      // In preview mode, show sample data instead of user-specific data
      if (previewMode) {
        setBookings([
          {
            id: 'preview-1',
            service_name: 'Home Cleaning',
            status: 'completed',
            scheduled_date: '2025-04-28',
            scheduled_time: '10:00 AM',
            total_amount: 75.00,
            address: '123 Main St, Tarboro, NC',
            worker_name: 'Maria Santos'
          },
          {
            id: 'preview-2',
            service_name: 'Private Concierge',
            status: 'confirmed',
            scheduled_date: '2025-05-05',
            scheduled_time: '2:00 PM',
            total_amount: 50.00,
            address: '456 Oak Ave, Tarboro, NC',
            worker_name: 'John Smith'
          },
          {
            id: 'preview-3',
            service_name: 'Local Delivery',
            status: 'pending',
            scheduled_date: '2025-05-10',
            scheduled_time: '9:00 AM',
            total_amount: 35.00,
            address: '789 Elm St, Tarboro, NC'
          }
        ]);
        setRecurringSeries([]);
        setLoyaltyAccount({ points: 150, tier: 'Silver' });
        setLoyaltyVouchers([]);
        setLoading(false);
        return;
      }
      
      // Fetch user-specific data for real customers
      const [bookingsRes, recurringRes, loyaltyRes, vouchersRes] = await Promise.all([
        bookingsAPI.getAll(),
        recurringAPI.getCustomerSeries().catch(() => ({ data: [] })),
        loyaltyAPI.getAccount().catch(() => ({ data: null })),
        loyaltyAPI.getVouchers().catch(() => ({ data: [] }))
      ]);
      setBookings(bookingsRes.data);
      setRecurringSeries(recurringRes.data || []);
      setLoyaltyAccount(loyaltyRes.data);
      setLoyaltyVouchers(vouchersRes.data || []);
      
      // Fetch active rides
      try {
        const ridesRes = await api.get('/rides/my-rides');
        const rides = ridesRes.data || [];
        // Filter to active rides only
        const active = rides.filter(r => 
          ['requested', 'driver_assigned', 'en_route_pickup', 'arrived_pickup', 'in_progress'].includes(r.status)
        );
        setActiveRides(active);
      } catch {
        // Silently ignore if rides not available
      }
      
      // Fetch unread message count
      try {
        const unreadRes = await messagingAPI.getUnreadCount();
        setUnreadMessages(unreadRes.data.unread_count);
      } catch {
        // Silently ignore - messaging may not be available
      }
      
      // Fetch conversations
      try {
        const convsRes = await messagingAPI.getConversations();
        setConversations(convsRes.data);
      } catch {
        // Silently ignore - conversations may not be available
      }
      
      // Check which completed bookings already have reviews
      const completedBookings = bookingsRes.data.filter(b => b.status === 'completed');
      const reviewChecks = {};
      for (const booking of completedBookings) {
        try {
          const reviewRes = await reviewsAPI.getBookingReview(booking.id);
          reviewChecks[booking.id] = reviewRes.data.has_review;
        } catch (e) {
          reviewChecks[booking.id] = false;
        }
      }
      setBookingReviews(reviewChecks);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cancel Ride handlers
  const handleCancelRide = (ride) => {
    setRideToCancel(ride);
    setShowRideCancelModal(true);
  };

  const confirmCancelRide = async () => {
    if (!rideToCancel) return;
    
    setCancellingRideId(rideToCancel.id);
    try {
      await api.put(`/rides/${rideToCancel.id}/cancel`);
      toast.success('Ride cancelled successfully');
      
      // Remove from active rides
      setActiveRides(prev => prev.filter(r => r.id !== rideToCancel.id));
      setShowRideCancelModal(false);
      setRideToCancel(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
    setCancellingRideId(null);
  };

  // Log contact attempts for activity tracking
  const logContactAttempt = async (rideId, contactType, direction) => {
    try {
      await api.post('/rides/log-contact', {
        ride_id: rideId,
        contact_type: contactType,
        contact_direction: direction
      });
    } catch (error) {
      // Silent fail - don't interrupt the call/text action
      console.error('Failed to log contact:', error);
    }
  };

  const openSupportChat = () => {
    setChatType('support');
    setChatBookingId(null);
    setShowChat(true);
  };

  const openBookingChat = (bookingId) => {
    setChatType('booking');
    setChatBookingId(bookingId);
    setShowChat(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Quick Rebook functions
  const openRebookModal = (booking) => {
    setRebookingBooking(booking);
    setRebookDate('');
    setRebookTime('');
    setRebookSameWorker(true);
    setShowRebookModal(true);
  };

  const handleQuickRebook = async () => {
    if (!rebookDate || !rebookTime) {
      toast.error('Please select a date and time');
      return;
    }
    setRebooking(true);
    try {
      const res = await customerAPI.quickRebook(
        rebookingBooking.id,
        rebookDate,
        rebookTime,
        rebookSameWorker
      );
      toast.success('Booking created! Redirecting to payment...');
      setShowRebookModal(false);
      // Navigate to payment page for the new booking
      navigate(`/customer/pay/${res.data.booking_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rebook');
    } finally {
      setRebooking(false);
    }
  };

  // Calendar Export
  const handleExportCalendar = async () => {
    try {
      const res = await customerAPI.exportCalendar();
      const blob = new Blob([res.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nchelphub_bookings.ics';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Calendar exported! Import it into your calendar app.');
    } catch (error) {
      toast.error('Failed to export calendar');
    }
  };

  // Add to favorites
  const handleAddToFavorites = async (booking) => {
    if (!booking.worker_id) {
      toast.error('No worker assigned to this booking');
      return;
    }
    try {
      await customerAPI.addFavorite(booking.worker_id, `Great service on ${booking.scheduled_date}`);
      toast.success(`${booking.worker_name} added to favorites!`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Worker is already in your favorites');
      } else {
        toast.error('Failed to add to favorites');
      }
    }
  };

  const fetchReferralData = async () => {
    setReferralLoading(true);
    try {
      const [codeRes, statsRes, referralsRes] = await Promise.all([
        referralsAPI.getMyCode(),
        referralsAPI.getStats().catch(() => ({ data: null })),
        referralsAPI.getMyReferrals().catch(() => ({ data: [] }))
      ]);
      setReferralCode(codeRes.data);
      setReferralStats(statsRes.data);
      setMyReferrals(referralsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  const copyReferralCode = async () => {
    if (referralCode?.code) {
      await navigator.clipboard.writeText(referralCode.code);
      setCopiedCode(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const shareReferralLink = async () => {
    const shareUrl = `${window.location.origin}/register?ref=${referralCode?.code}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join NC Help Hub!',
          text: `Use my referral code ${referralCode?.code} to get $10 off your first booking!`,
          url: shareUrl,
        });
      } catch (e) {
        // User cancelled share
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Referral link copied!');
    }
  };

  // Feedback functions
  const fetchFeedbackData = async () => {
    setFeedbackLoading(true);
    try {
      const [toReviewRes, myReviewsRes] = await Promise.all([
        reviewsAPI.getBookingsToReview(),
        reviewsAPI.getMyReviews()
      ]);
      setBookingsToReview(toReviewRes.data || []);
      setMyReviews(myReviewsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch feedback data:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewForm.booking_id || !reviewForm.rating) {
      toast.error('Please select a booking and rating');
      return;
    }
    setSubmittingReview(true);
    try {
      await reviewsAPI.submitReview(reviewForm.booking_id, reviewForm.rating, reviewForm.comment);
      toast.success('Thank you for your feedback!');
      setReviewForm({ booking_id: '', rating: 5, comment: '' });
      fetchFeedbackData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={`star-${i}`} 
        className={`w-6 h-6 cursor-${interactive ? 'pointer' : 'default'} ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        onClick={() => interactive && onChange && onChange(i + 1)}
      />
    ));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'badge-warning',
      confirmed: 'badge-primary',
      in_progress: 'badge-primary',
      completed: 'badge-success',
      cancelled: 'badge-error'
    };
    return styles[status] || 'badge-primary';
  };

  // Download PDF Invoice
  const handleDownloadInvoice = async (bookingId) => {
    try {
      const response = await paymentsAPI.getInvoice(bookingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NC_Help_Hub_Invoice_${bookingId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  // Download PDF Receipt
  const handleDownloadReceipt = async (bookingId) => {
    try {
      const response = await paymentsAPI.getReceipt(bookingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NC_Help_Hub_Receipt_${bookingId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('No payments found for this booking');
      } else {
        toast.error('Failed to download receipt');
      }
    }
  };

  const openReviewModal = (booking) => {
    setReviewBooking(booking);
    setReviewRating(5);
    setReviewComment('');
    setShowReviewModal(true);
  };

  const submitReview = async () => {
    if (!reviewBooking) return;
    
    setSubmittingReview(true);
    try {
      await reviewsAPI.create({
        booking_id: reviewBooking.id,
        rating: reviewRating,
        comment: reviewComment || null
      });
      toast.success('Thank you for your review!');
      setShowReviewModal(false);
      setBookingReviews(prev => ({ ...prev, [reviewBooking.id]: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const StarRating = ({ rating, setRating, interactive = true }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && setRating(star)}
            disabled={!interactive}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              className={`w-8 h-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Tip worker functions
  const openTipModal = (booking) => {
    setTipBooking(booking);
    setShowTipModal(true);
  };

  const handleTipSuccess = () => {
    // Mark booking as tipped
    setBookingTips(prev => ({ ...prev, [tipBooking?.id]: true }));
    setShowTipModal(false);
    setTipBooking(null);
  };

  // Preview mode action handler - shows toast instead of navigating
  const handlePreviewAction = (actionName) => {
    toast.info(`${actionName} is disabled in preview mode`, {
      description: 'This is a preview of the customer dashboard'
    });
  };

  // Helper component for Book Now button that respects preview mode
  const BookNowButton = ({ serviceId, className = "btn-primary w-full", buttonClassName = "" }) => {
    if (previewMode) {
      return (
        <Button 
          onClick={() => handlePreviewAction('Booking')} 
          className={className || buttonClassName}
          data-testid={`book-btn-${serviceId}`}
        >
          Book Now <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      );
    }
    return (
      <Link to={`/customer/book/${serviceId}`}>
        <Button className={className || buttonClassName} data-testid={`book-btn-${serviceId}`}>
          Book Now <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
    );
  };

  // Recurring booking management
  const handlePauseRecurring = async (seriesId) => {
    try {
      await recurringAPI.pause(seriesId);
      toast.success('Recurring bookings paused');
      fetchData();
    } catch (error) {
      toast.error('Failed to pause recurring bookings');
    }
  };

  const handleResumeRecurring = async (seriesId) => {
    try {
      await recurringAPI.resume(seriesId);
      toast.success('Recurring bookings resumed');
      fetchData();
    } catch (error) {
      toast.error('Failed to resume recurring bookings');
    }
  };

  const handleCancelRecurring = async (seriesId) => {
    if (!window.confirm('Are you sure you want to cancel all future bookings in this series?')) {
      return;
    }
    try {
      await recurringAPI.cancelFuture(seriesId);
      toast.success('Future recurring bookings cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel recurring bookings');
    }
  };

  // Cancel booking by customer
  const handleCancelBooking = async () => {
    if (!cancellingBooking) return;
    
    setIsCancelling(true);
    try {
      await customerAPI.cancelBooking(cancellingBooking.id, cancelReason);
      toast.success('Booking cancelled successfully');
      setShowCancelModal(false);
      setCancellingBooking(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };

  const openCancelModal = (booking) => {
    setCancellingBooking(booking);
    setShowCancelModal(true);
  };

  // Service search and filter
  const handleServiceSearch = (query) => {
    setSearchQuery(query);
    filterServices(query, priceRange);
  };

  const filterServices = (query, prices) => {
    let filtered = services;
    
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(lowerQuery) ||
        s.description?.toLowerCase().includes(lowerQuery)
      );
    }
    
    if (prices.min) {
      filtered = filtered.filter(s => s.hourly_rate >= parseFloat(prices.min));
    }
    
    if (prices.max) {
      filtered = filtered.filter(s => s.hourly_rate <= parseFloat(prices.max));
    }
    
    setFilteredServices(filtered);
  };

  // Export booking history
  const handleExportBookings = async (format = 'csv') => {
    try {
      const response = await customerAPI.exportBookings({ format });
      const blob = new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking_history.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Booking history exported');
    } catch (error) {
      toast.error('Failed to export bookings');
    }
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      'weekly': 'Weekly',
      'bi_weekly': 'Every 2 Weeks',
      'monthly': 'Monthly'
    };
    return labels[freq] || freq;
  };

  // Loyalty Program Functions
  const handleRedeemReward = async (rewardType) => {
    setRedeemingReward(rewardType);
    try {
      const response = await loyaltyAPI.redeem(rewardType);
      toast.success(response.data.message);
      // Refresh loyalty data
      const [loyaltyRes, vouchersRes] = await Promise.all([
        loyaltyAPI.getAccount(),
        loyaltyAPI.getVouchers()
      ]);
      setLoyaltyAccount(loyaltyRes.data);
      setLoyaltyVouchers(vouchersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem reward');
    } finally {
      setRedeemingReward(null);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'bronze': 'bg-amber-600',
      'silver': 'bg-gray-400',
      'gold': 'bg-amber-500',
      'platinum': 'bg-purple-600'
    };
    return colors[tier] || 'bg-gray-400';
  };

  const getTierBgColor = (tier) => {
    const colors = {
      'bronze': 'bg-[var(--warning)]/10 border-[var(--warning)]/30',
      'silver': 'bg-[var(--background-subtle)] border-[var(--border-primary)]',
      'gold': 'bg-[var(--warning)]/10 border-[var(--warning)]/30',
      'platinum': 'bg-[#F4C2C2]/10 border-[#F4C2C2]/30'
    };
    return colors[tier] || 'bg-[var(--background-subtle)]';
  };

  // Primary 5 service categories
  // 1. Cleaning services (excluding add-ons)
  const cleaningServicesList = services.filter(s => s.service_type === 'cleaning' && s.package_type !== 'add_on');
  const cleaningAddons = services.filter(s => s.service_type === 'cleaning' && s.package_type === 'add_on');
  // 2. Laundry services
  const laundryServicesList = services.filter(s => s.service_type === 'laundry');
  // 3. Courier services
  const courierServicesList = services.filter(s => s.service_type === 'courier');
  // 4. Errands services
  const errandServicesList = services.filter(s => s.service_type === 'errands');
  // 5. Tasks services
  const taskServicesList = services.filter(s => s.service_type === 'tasks');
  // 6. Private Concierge services
  const privateConciergeServices = services.filter(s => s.service_type === 'concierge' || s.category === 'concierge');

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Preview Mode Banner */}
      {previewMode && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 text-center text-sm font-medium sticky top-0 z-50 flex items-center justify-center gap-4">
          <span>
            <span className="mr-2">👁️</span>
            PREVIEW MODE - This is how customers see their dashboard
            <span className="ml-2">👁️</span>
          </span>
          <button
            onClick={() => window.location.href = '/owner/dashboard'}
            className="px-3 py-1 bg-white/20 rounded text-sm font-medium hover:bg-white/30 transition-colors"
          >
            ← Back to Owner Dashboard
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="nav-glass sticky top-0 z-40 px-6 py-4" style={{ top: previewMode ? '36px' : '0' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-xl flex items-center justify-center">
              <span className="text-black font-bold text-lg">H</span>
            </div>
            <span className="text-xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">NC Help Hub</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-[var(--text-secondary)]">Welcome, {previewMode ? 'Sample Customer' : user?.full_name}</span>
            {!previewMode && <NotificationBell />}
            {!previewMode && (
              <Button variant="ghost" onClick={handleLogout} className="text-[var(--text-muted)]" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Notification Permission Prompt */}
      {showNotificationPrompt && (
        <NotificationPrompt 
          onDismiss={() => {
            setShowNotificationPrompt(false);
            localStorage.setItem('notificationPromptDismissed', 'true');
          }} 
        />
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-['Cormorant_Garamond']">My Dashboard</h1>
          <p className="text-[var(--text-secondary)] mt-1">Browse services and manage your bookings</p>
        </div>

        {/* Tab Navigation - Mobile Friendly */}
        <div className="mb-6 pb-4 border-b border-[var(--border-primary)]">
          {/* Mobile: Bottom Nav handles navigation - hide legacy indicator */}
          <div className="hidden md:hidden flex items-center gap-2 pb-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--primary)]/10 rounded-full border border-[var(--primary)]/30">
              {(() => {
                const tabIcons = {
                  services: Sparkles, bookings: Calendar, messages: MessageCircle,
                  incidents: AlertTriangle, loyalty: Gift, documents: FileSignature,
                  favorites: Heart, payments: CreditCard, uploads: Upload, profile: User,
                  customrequests: Plus, reviews: Star
                };
                const TabIcon = tabIcons[activeTab] || Sparkles;
                return <TabIcon className="w-4 h-4 text-[var(--primary)]" />;
              })()}
              <span className="text-sm font-medium text-[var(--primary)] capitalize">
                {activeTab === 'customrequests' ? 'Request' : activeTab}
              </span>
            </div>
            <span className="text-xs text-[var(--text-muted)]">Swipe up for more options</span>
          </div>

          {/* Desktop: Organized by workflow */}
          <div className="hidden md:flex gap-1 items-center flex-wrap">
            {/* SERVICES */}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-2">Services</span>
            <button
              onClick={() => setActiveTab('services')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'services' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
              data-testid="tab-services-desktop"
            >
              <Sparkles className="w-4 h-4 inline mr-1" />
              Browse
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'bookings' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
              data-testid="tab-bookings-desktop"
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Bookings
            </button>
            {recurringSeries.length > 0 && (
              <button
                onClick={() => setActiveTab('recurring')}
                className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                  activeTab === 'recurring' 
                    ? 'bg-[var(--primary)] text-white' 
                    : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
                }`}
              >
                <Repeat className="w-4 h-4 inline mr-1" />
                Recurring
              </button>
            )}
            <button
              onClick={() => setActiveTab('customrequests')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'customrequests' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
              data-testid="tab-customrequests"
            >
              <Sparkles className="w-4 h-4 inline mr-1" />
              Request
            </button>
            <button
              onClick={() => setActiveTab('servicerequests')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'servicerequests' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
              data-testid="tab-servicerequests"
            >
              <FileText className="w-4 h-4 inline mr-1" />
              Service Forms
            </button>
            <button
              onClick={() => setActiveTab('rides')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'rides' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
              data-testid="tab-rides"
            >
              <Car className="w-4 h-4 inline mr-1" />
              Book Ride
            </button>

            <span className="text-gray-300 mx-1">|</span>

            {/* SUPPORT */}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-2">Support</span>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'messages' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <MessageCircle className="w-4 h-4 inline mr-1" />
              Messages
              {unreadMessages > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{unreadMessages}</span>}
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'feedback' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Feedback
            </button>
            <button
              onClick={() => setActiveTab('incidents')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'incidents' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Report Issue
            </button>

            <span className="text-gray-300 mx-1">|</span>

            {/* ACCOUNT */}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-2">Account</span>
            <button
              onClick={() => setActiveTab('loyalty')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'loyalty' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <Gift className="w-4 h-4 inline mr-1" />
              Rewards
            </button>
            <button
              onClick={() => setActiveTab('referrals')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'referrals' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <Share2 className="w-4 h-4 inline mr-1" />
              Refer
            </button>
            <button
              onClick={() => setActiveTab('favorites')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'favorites' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <Heart className="w-4 h-4 inline mr-1" />
              Favorites
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'payments' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <CreditCard className="w-4 h-4 inline mr-1" />
              Payments
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'documents' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              <FileSignature className="w-4 h-4 inline mr-1" />
              Documents
            </button>
            <button
              onClick={() => setActiveTab('uploads')}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'uploads' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
              data-testid="tab-uploads-desktop"
            >
              <Upload className="w-4 h-4 inline mr-1" />
              Uploads
            </button>
          </div>
        </div>

        {/* Upcoming Appointment Card - Show on Services tab */}
        {activeTab === 'services' && (
          <UpcomingAppointmentCard onMessageWorker={openBookingChat} />
        )}

        {/* Quick Pay Alert - Show if there are pending payments */}
        {activeTab === 'services' && bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-[var(--warning)]/30 rounded-xl shadow-sm"
            data-testid="quick-pay-alert"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">
                    You have {bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length} pending payment{bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-amber-600">
                    Total: ${bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').reduce((sum, b) => sum + (b.total_amount || 0), 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setActiveTab('bookings')}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                  data-testid="view-pending-payments"
                >
                  View All
                </Button>
                {bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled')[0] && (
                  previewMode ? (
                    <Button 
                      onClick={() => handlePreviewAction('Payment')} 
                      className="bg-amber-500 hover:bg-amber-600 text-white" 
                      data-testid="quick-pay-now"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay Now
                    </Button>
                  ) : (
                    <Link to={`/customer/pay/${bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled')[0].id}`}>
                      <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="quick-pay-now">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay Now
                      </Button>
                    </Link>
                  )
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Service Search & Filter */}
            <div className="mb-8 card p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                  <Input
                    placeholder="Search services..."
                    value={searchQuery}
                    onChange={(e) => handleServiceSearch(e.target.value)}
                    className="pl-10"
                    data-testid="service-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min $"
                    value={priceRange.min}
                    onChange={(e) => {
                      const newRange = { ...priceRange, min: e.target.value };
                      setPriceRange(newRange);
                      filterServices(searchQuery, newRange);
                    }}
                    className="w-24"
                    data-testid="price-min-filter"
                  />
                  <Input
                    type="number"
                    placeholder="Max $"
                    value={priceRange.max}
                    onChange={(e) => {
                      const newRange = { ...priceRange, max: e.target.value };
                      setPriceRange(newRange);
                      filterServices(searchQuery, newRange);
                    }}
                    className="w-24"
                    data-testid="price-max-filter"
                  />
                  {(searchQuery || priceRange.min || priceRange.max) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchQuery('');
                        setPriceRange({ min: '', max: '' });
                        setFilteredServices([]);
                      }}
                      data-testid="clear-filters"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {(searchQuery || priceRange.min || priceRange.max) && (
                <p className="text-sm text-[var(--text-muted)] mt-2">
                  Showing {filteredServices.length} of {services.length} services
                </p>
              )}
            </div>

            {/* Cleaning Services */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[var(--secondary)]/10 rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[var(--secondary)]" />
                </div>
                <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Cleaning Services</h2>
                <span className="text-xs bg-[var(--secondary)]/20 text-[var(--secondary)] px-2 py-1 rounded-full">{cleaningServicesList.length} services</span>
              </div>
              
              {cleaningServicesList.length === 0 ? (
                <div className="card p-8 text-center">
                  <p className="text-[var(--text-muted)]">No cleaning services available yet. Check back soon!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cleaningServicesList.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ y: -4 }}
                      className="card p-6 cursor-pointer hover:ring-2 hover:ring-[#D4AF37] transition-all"
                      data-testid={`service-card-${service.id}`}
                      onClick={() => previewMode ? handlePreviewAction('Book Service') : navigate(`/customer/book/${service.id}`)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          service.package_type === 'recurring' ? 'bg-[var(--success)]/15 text-green-700' :
                          service.package_type === 'package' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                          'bg-[var(--accent)] text-[var(--primary)]'
                        }`}>
                          {service.package_type === 'recurring' ? '🔄 Recurring' : 
                           service.package_type === 'package' ? '📦 Package' : 'Hourly'}
                        </span>
                        {service.recurring_discount > 0 && (
                          <span className="text-[var(--success)] text-sm font-semibold">
                            {service.recurring_discount}% off
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                      <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {service.duration_hours}h
                        </span>
                        <span className="font-bold text-[var(--secondary)] text-lg">
                          ${service.hourly_rate}/hr
                        </span>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <BookNowButton 
                          serviceId={service.id} 
                          className="bg-[#D4AF37] hover:bg-[#E6C965] text-black font-semibold rounded-full w-full" 
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Cleaning Add-Ons */}
            {cleaningAddons.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Plus className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Cleaning Add-Ons</h2>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">{cleaningAddons.length} add-ons</span>
                </div>
                <p className="text-[var(--text-muted)] text-sm mb-6 -mt-4 ml-13">
                  Enhance your cleaning with these extra services. Add-ons can be requested during booking.
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {cleaningAddons.map((addon) => (
                    <motion.div
                      key={addon.id}
                      whileHover={{ y: -2 }}
                      className="card p-4 border-2 border-[var(--warning)]/30 bg-[var(--warning)]/10/30"
                      data-testid={`addon-card-${addon.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                          Add-On
                        </span>
                      </div>
                      <h3 className="text-base font-bold mb-1">{addon.name.replace(' (Add-On)', '')}</h3>
                      <p className="text-[var(--text-secondary)] text-xs mb-3 line-clamp-2">{addon.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--text-muted)]">
                          {addon.duration_hours > 0 ? `${addon.duration_hours}h` : 'Flat fee'}
                        </span>
                        <span className="font-bold text-amber-600">
                          ${addon.duration_hours > 0 ? `${addon.hourly_rate}/hr` : addon.hourly_rate}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Laundry Services */}
            {laundryServicesList.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#F4C2C2]/15 rounded-full flex items-center justify-center">
                    <Shirt className="w-5 h-5 text-[#F4C2C2]" />
                  </div>
                  <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Laundry Services</h2>
                  <span className="text-xs bg-[#F4C2C2]/20 text-pink-700 px-2 py-1 rounded-full">{laundryServicesList.length} services</span>
                </div>
                <p className="text-[var(--text-muted)] text-sm mb-6 -mt-4 ml-13">
                  Wash & fold, dry cleaning, ironing, pickup & delivery
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {laundryServicesList.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ y: -4 }}
                      className="card p-6 border-2 border-[#F4C2C2]/30 hover:border-pink-300"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-xs px-2 py-1 rounded-full bg-[#F4C2C2]/15 text-pink-700">Laundry</span>
                        {service.recurring_discount > 0 && (
                          <span className="text-[var(--success)] text-sm font-semibold">{service.recurring_discount}% off</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                      <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{service.duration_hours}h</span>
                        <span className="font-bold text-[#F4C2C2] text-lg">${service.hourly_rate}/hr</span>
                      </div>
                      <BookNowButton serviceId={service.id} />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Courier Services */}
            {courierServicesList.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#D4AF37]/15 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Courier Services</h2>
                  <span className="text-xs bg-[#D4AF37]/20 text-amber-700 px-2 py-1 rounded-full">{courierServicesList.length} services</span>
                </div>
                <p className="text-[var(--text-muted)] text-sm mb-6 -mt-4 ml-13">
                  Local delivery, document transport, package delivery, same-day service
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courierServicesList.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ y: -4 }}
                      className="card p-6 border-2 border-[#D4AF37]/30 hover:border-amber-300"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-xs px-2 py-1 rounded-full bg-[#D4AF37]/15 text-amber-700">Courier</span>
                        {service.recurring_discount > 0 && (
                          <span className="text-[var(--success)] text-sm font-semibold">{service.recurring_discount}% off</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                      <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{service.duration_hours}h</span>
                        <span className="font-bold text-[#D4AF37] text-lg">${service.hourly_rate}/hr</span>
                      </div>
                      <BookNowButton serviceId={service.id} />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Private Concierge Services */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[var(--accent)] rounded-full flex items-center justify-center">
                  <HeartHandshake className="w-5 h-5 text-[var(--primary)]" />
                </div>
                <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Private Concierge</h2>
                <span className="text-xs bg-[var(--accent)] text-[var(--primary)] px-2 py-1 rounded-full">{privateConciergeServices.length} services</span>
              </div>
              <p className="text-[var(--text-muted)] text-sm mb-6 -mt-4 ml-13">
                Personal assistance, meal prep, transportation, light housekeeping, respite care
              </p>
              
              {privateConciergeServices.length === 0 ? (
                <div className="card p-8 text-center">
                  <p className="text-[var(--text-muted)]">No private concierge services available yet. Check back soon!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {privateConciergeServices.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ y: -4 }}
                      className="card p-6"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          service.package_type === 'recurring' ? 'bg-[var(--success)]/15 text-green-700' :
                          service.package_type === 'package' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                          'bg-[var(--accent)] text-[var(--primary)]'
                        }`}>
                          {service.package_type === 'recurring' ? '🔄 Recurring' : 
                           service.package_type === 'package' ? '📦 Package' : 'Concierge'}
                        </span>
                        {service.recurring_discount > 0 && (
                          <span className="text-[var(--success)] text-sm font-semibold">
                            {service.recurring_discount}% off
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                      <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {service.duration_hours}h
                        </span>
                        <span className="font-bold text-[var(--primary)] text-lg">
                          ${service.hourly_rate}/hr
                        </span>
                      </div>
                      <BookNowButton serviceId={service.id} />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Errands Services */}
            {errandServicesList.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Errands</h2>
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">{errandServicesList.length} services</span>
                </div>
                <p className="text-[var(--text-muted)] text-sm mb-6 -mt-4 ml-13">
                  Grocery pickup, store runs, prescription pickup, returns & more
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {errandServicesList.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ y: -4 }}
                      className="card p-6 border-2 border-orange-200 hover:border-orange-300"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">Errands</span>
                        {service.recurring_discount > 0 && (
                          <span className="text-[var(--success)] text-sm font-semibold">{service.recurring_discount}% off</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                      <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{service.duration_hours}h</span>
                        <span className="font-bold text-orange-600 text-lg">${service.hourly_rate}/hr</span>
                      </div>
                      <BookNowButton serviceId={service.id} />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Task Services */}
            {taskServicesList.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[var(--success)]/15 rounded-full flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-[var(--success)]" />
                  </div>
                  <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Task Services</h2>
                  <span className="text-xs bg-[var(--success)]/15 text-green-700 px-2 py-1 rounded-full">{taskServicesList.length} services</span>
                </div>
                <p className="text-[var(--text-muted)] text-sm mb-6 -mt-4 ml-13">
                  Furniture assembly, home organization, moving help, yard work & more
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {taskServicesList.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ y: -4 }}
                      className="card p-6 border-2 border-[var(--success)]/30 hover:border-green-300"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-xs px-2 py-1 rounded-full bg-[var(--success)]/15 text-green-700">Tasks</span>
                        {service.recurring_discount > 0 && (
                          <span className="text-[var(--success)] text-sm font-semibold">{service.recurring_discount}% off</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                      <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{service.duration_hours}h</span>
                        <span className="font-bold text-[var(--success)] text-lg">${service.hourly_rate}/hr</span>
                      </div>
                      <BookNowButton serviceId={service.id} />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Header with Export Button */}
            {bookings.length > 0 && !previewMode && (
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">My Booking History</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportBookings('csv')}
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportBookings('json')}
                    data-testid="export-json-btn"
                  >
                    <FileText className="w-4 h-4 mr-1" /> Export JSON
                  </Button>
                </div>
              </div>
            )}
            
            {bookings.length === 0 ? (
              <div className="card p-12 text-center">
                <Calendar className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">No Bookings Yet</h3>
                <p className="text-[var(--text-secondary)] mb-6">Browse our services and book your first appointment!</p>
                <Button onClick={() => setActiveTab('services')} className="btn-primary">
                  Browse Services
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card p-6"
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold">{booking.service_name}</h3>
                          <span className={getStatusBadge(booking.status)}>
                            {booking.status.replace('_', ' ')}
                          </span>
                          {booking.recurring_series_id && (
                            <span className="badge-primary flex items-center gap-1 text-xs">
                              <Repeat className="w-3 h-3" />
                              Recurring
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[var(--text-secondary)]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {booking.scheduled_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {booking.scheduled_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {booking.city}, {booking.state}
                          </span>
                        </div>
                        {booking.worker_name && (
                          <p className="text-sm text-[var(--text-muted)] mt-2">
                            Worker: {booking.worker_name}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[var(--primary)]">${Number(booking.total_amount || 0).toFixed(2)}</p>
                        <span className={`text-sm ${booking.payment_status === 'paid' ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>
                          {booking.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                        </span>
                        {booking.payment_status !== 'paid' && booking.status !== 'cancelled' && (
                          previewMode ? (
                            <Button 
                              onClick={() => handlePreviewAction('Payment')} 
                              className="btn-primary mt-2" 
                              size="sm" 
                              data-testid={`pay-btn-${booking.id}`}
                            >
                              Pay Now
                            </Button>
                          ) : (
                            <Link to={`/customer/pay/${booking.id}`}>
                              <Button className="btn-primary mt-2" size="sm" data-testid={`pay-btn-${booking.id}`}>
                                Pay Now
                              </Button>
                            </Link>
                          )
                        )}
                        {booking.status === 'completed' && booking.payment_status === 'paid' && !bookingReviews[booking.id] && (
                          <Button 
                            onClick={() => previewMode ? handlePreviewAction('Reviews') : openReviewModal(booking)} 
                            className="mt-2 bg-amber-500 hover:bg-yellow-600 text-white" 
                            size="sm"
                            data-testid={`review-btn-${booking.id}`}
                          >
                            <Star className="w-4 h-4 mr-1" /> Leave Review
                          </Button>
                        )}
                        {bookingReviews[booking.id] && (
                          <span className="text-sm text-[var(--success)] flex items-center gap-1 mt-2">
                            <Star className="w-4 h-4 fill-current" /> Reviewed
                          </span>
                        )}
                        {/* Message Worker Button - show if worker assigned and not cancelled */}
                        {booking.worker_id && booking.status !== 'cancelled' && (
                          <Button 
                            onClick={() => previewMode ? handlePreviewAction('Messaging') : openBookingChat(booking.id)} 
                            className="mt-2 btn-secondary" 
                            size="sm"
                            data-testid={`message-worker-${booking.id}`}
                          >
                            <MessageCircle className="w-4 h-4 mr-1" /> Message Worker
                          </Button>
                        )}
                        {/* Quick Rebook Button - show for completed bookings */}
                        {booking.status === 'completed' && (
                          <Button 
                            onClick={() => previewMode ? handlePreviewAction('Rebooking') : openRebookModal(booking)} 
                            variant="outline"
                            className="mt-2" 
                            size="sm"
                            data-testid={`rebook-btn-${booking.id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" /> Rebook
                          </Button>
                        )}
                        {/* Download Invoice Button - show for completed paid bookings */}
                        {booking.status === 'completed' && booking.payment_status === 'paid' && (
                          <Button 
                            onClick={() => handleDownloadInvoice(booking.id)} 
                            variant="outline"
                            className="mt-2 border-[var(--primary)] text-[var(--primary)]" 
                            size="sm"
                            data-testid={`invoice-btn-${booking.id}`}
                          >
                            <FileText className="w-4 h-4 mr-1" /> Invoice
                          </Button>
                        )}
                        {/* Download Receipt Button - show for paid bookings */}
                        {(booking.payment_status === 'paid' || booking.payment_status === 'deposit_paid') && (
                          <Button 
                            onClick={() => handleDownloadReceipt(booking.id)} 
                            variant="outline"
                            className="mt-2 border-[var(--success)] text-[var(--success)]" 
                            size="sm"
                            data-testid={`receipt-btn-${booking.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> Receipt
                          </Button>
                        )}
                        {/* Add to Favorites Button - show if worker assigned */}
                        {booking.worker_id && booking.status === 'completed' && (
                          <Button 
                            onClick={() => previewMode ? handlePreviewAction('Favorites') : handleAddToFavorites(booking)} 
                            variant="outline"
                            className="mt-2" 
                            size="sm"
                            data-testid={`favorite-btn-${booking.id}`}
                          >
                            <Heart className="w-4 h-4 mr-1" /> Favorite Worker
                          </Button>
                        )}
                        {/* Tip Worker Button - show for completed paid bookings with worker */}
                        {booking.worker_id && booking.status === 'completed' && booking.payment_status === 'paid' && !bookingTips[booking.id] && (
                          <Button 
                            onClick={() => previewMode ? handlePreviewAction('Tipping') : openTipModal(booking)} 
                            variant="outline"
                            className="mt-2 border-pink-300 text-pink-600 hover:bg-pink-50" 
                            size="sm"
                            data-testid={`tip-btn-${booking.id}`}
                          >
                            <Heart className="w-4 h-4 mr-1" /> Leave a Tip
                          </Button>
                        )}
                        {bookingTips[booking.id] && (
                          <span className="text-sm text-pink-600 flex items-center gap-1 mt-2">
                            <Heart className="w-4 h-4 fill-current" /> Tip Sent
                          </span>
                        )}
                        {/* Cancel Booking Button - show for pending/confirmed bookings */}
                        {['pending', 'confirmed'].includes(booking.status) && (
                          <Button 
                            onClick={() => previewMode ? handlePreviewAction('Cancellation') : openCancelModal(booking)} 
                            variant="outline"
                            className="mt-2 border-red-300 text-[var(--error)] hover:bg-[var(--error)]/10" 
                            size="sm"
                            data-testid={`cancel-btn-${booking.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Cancel Booking
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Calendar Export Button */}
            {bookings.length > 0 && (
              <div className="mt-6 pt-6 border-t border-[var(--border-primary)]">
                <Button onClick={handleExportCalendar} variant="outline" data-testid="export-calendar-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export to Calendar
                </Button>
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  Download your bookings as an iCal file to import into Google Calendar, Apple Calendar, or Outlook.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="messages-tab">
            <div className="mb-6">
              <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Messages</h2>
              <p className="text-[var(--text-muted)] text-sm mt-1">Chat with support or your assigned workers</p>
            </div>
            <ChatInterface 
              user={user} 
              userRole="customer"
              height="calc(100vh - 280px)"
            />
          </motion.div>
        )}

        {/* Recurring Tab */}
        {activeTab === 'recurring' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Recurring Bookings</h2>
            </div>

            <div className="grid gap-4">
              {recurringSeries.map((series) => (
                <motion.div
                  key={series.series_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card p-6"
                  data-testid={`recurring-series-${series.series_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-[var(--accent)] rounded-full flex items-center justify-center">
                          <Repeat className="w-5 h-5 text-[var(--primary)]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">{series.service_name}</h3>
                          <p className="text-sm text-[var(--primary)] font-medium">
                            {getFrequencyLabel(series.frequency)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-[var(--text-secondary)] mt-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Next: {series.next_booking_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <History className="w-4 h-4" />
                          {series.total_bookings} total bookings
                        </span>
                        {series.end_date && (
                          <span className="text-[var(--text-muted)]">
                            Ends: {series.end_date}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePauseRecurring(series.series_id)}
                        className="flex items-center gap-1"
                        data-testid={`pause-${series.series_id}`}
                      >
                        <Pause className="w-4 h-4" />
                        Pause
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelRecurring(series.series_id)}
                        className="flex items-center gap-1 text-[var(--error)] hover:text-red-700 hover:border-red-300"
                        data-testid={`cancel-${series.series_id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel Series
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {recurringSeries.length === 0 && (
                <div className="card p-12 text-center">
                  <Repeat className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">No Recurring Bookings</h3>
                  <p className="text-[var(--text-secondary)]">
                    When you book a service with recurring option, it will appear here.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Loyalty Tab */}
        {activeTab === 'loyalty' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Rewards Program</h2>
            </div>

            {loyaltyAccount ? (
              <div className="space-y-6">
                {/* Points Card */}
                <div className={`card p-6 border-2 ${getTierBgColor(loyaltyAccount.tier)}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 ${getTierColor(loyaltyAccount.tier)} rounded-full flex items-center justify-center`}>
                        <Award className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-[var(--text-muted)]">Your Status</p>
                        <h3 className="text-2xl font-bold capitalize">{loyaltyAccount.tier} Member</h3>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {loyaltyAccount.total_points_earned} lifetime points earned
                        </p>
                      </div>
                    </div>
                    <div className="text-center md:text-right">
                      <p className="text-sm text-[var(--text-muted)]">Available Points</p>
                      <p className="text-4xl font-bold text-[var(--primary)]">{loyaltyAccount.points_balance}</p>
                    </div>
                  </div>
                  
                  {/* Progress to next tier */}
                  {loyaltyAccount.next_tier && (
                    <div className="mt-6 pt-6 border-t border-[var(--border-primary)]">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="capitalize">{loyaltyAccount.tier}</span>
                        <span className="capitalize">{loyaltyAccount.next_tier}</span>
                      </div>
                      <div className="w-full bg-[var(--background-subtle)] rounded-full h-3">
                        <div 
                          className={`${getTierColor(loyaltyAccount.tier)} h-3 rounded-full transition-all`}
                          style={{ width: `${loyaltyAccount.tier_progress}%` }}
                        />
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-2 text-center">
                        {loyaltyAccount.points_to_next_tier} more points to {loyaltyAccount.next_tier}
                      </p>
                    </div>
                  )}
                </div>

                {/* How to Earn */}
                <div className="card p-6">
                  <h3 className="font-bold flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-[var(--primary)]" />
                    How to Earn Points
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-[var(--background-subtle)] rounded-xl text-center">
                      <p className="text-2xl font-bold text-[var(--primary)]">1 pt</p>
                      <p className="text-sm text-[var(--text-secondary)]">per $1 spent</p>
                    </div>
                    <div className="p-4 bg-[var(--background-subtle)] rounded-xl text-center">
                      <p className="text-2xl font-bold text-[var(--primary)]">2x pts</p>
                      <p className="text-sm text-[var(--text-secondary)]">on recurring bookings</p>
                    </div>
                    <div className="p-4 bg-[var(--background-subtle)] rounded-xl text-center">
                      <p className="text-2xl font-bold text-[var(--primary)]">50 pts</p>
                      <p className="text-sm text-[var(--text-secondary)]">per referral</p>
                    </div>
                  </div>
                </div>

                {/* Available Rewards */}
                <div className="card p-6">
                  <h3 className="font-bold flex items-center gap-2 mb-4">
                    <Gift className="w-5 h-5 text-[var(--primary)]" />
                    Redeem Rewards
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {loyaltyAccount.rewards_available?.map((reward) => (
                      <div 
                        key={reward.type}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          reward.can_redeem 
                            ? 'border-[var(--primary)] bg-[var(--accent)]' 
                            : 'border-[var(--border-primary)] bg-[var(--background-subtle)] opacity-60'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-bold">{reward.name}</h4>
                          <span className="text-sm font-medium text-[var(--primary)]">
                            {reward.points_cost} pts
                          </span>
                        </div>
                        <Button
                          onClick={() => handleRedeemReward(reward.type)}
                          disabled={!reward.can_redeem || redeemingReward === reward.type}
                          className={`w-full mt-2 ${reward.can_redeem ? 'btn-primary' : 'bg-gray-300 cursor-not-allowed'}`}
                          size="sm"
                          data-testid={`redeem-${reward.type}`}
                        >
                          {redeemingReward === reward.type ? 'Redeeming...' : 'Redeem'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active Vouchers */}
                {loyaltyVouchers.length > 0 && (
                  <div className="card p-6">
                    <h3 className="font-bold flex items-center gap-2 mb-4">
                      <CreditCard className="w-5 h-5 text-[var(--primary)]" />
                      Your Vouchers
                    </h3>
                    <div className="space-y-3">
                      {loyaltyVouchers.map((voucher) => (
                        <div 
                          key={voucher.id}
                          className="p-4 bg-gradient-to-r from-[var(--primary)]/10 to-[var(--secondary)]/10 rounded-xl flex items-center justify-between"
                        >
                          <div>
                            <p className="font-bold">{voucher.reward_name}</p>
                            <p className="text-sm text-[var(--text-muted)]">
                              Code: <span className="font-mono font-bold">{voucher.id}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-[var(--text-muted)]">Expires</p>
                            <p className="text-sm">{new Date(voucher.expires_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="card p-12 text-center">
                <Gift className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">Start Earning Rewards!</h3>
                <p className="text-[var(--text-secondary)]">
                  Book your first service to start earning loyalty points.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="max-w-3xl mx-auto">
              {/* Hero Section */}
              <div className="card p-8 text-center bg-gradient-to-br from-[var(--primary-light)] to-[var(--background-paper)] mb-8">
                <Share2 className="w-12 h-12 text-[var(--primary)] mx-auto mb-4" />
                <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-2">
                  Refer Friends, Earn $10!
                </h2>
                <p className="text-[var(--text-secondary)] mb-6">
                  Share your referral code with friends. They get $10 off their first booking, and you earn $10 in credit!
                </p>

                {referralLoading ? (
                  <div className="animate-pulse bg-[var(--background-subtle)] h-16 rounded-xl"></div>
                ) : referralCode ? (
                  <div className="bg-[var(--background-paper)] rounded-xl p-6 border-2 border-dashed border-[var(--primary)]">
                    <p className="text-sm text-[var(--text-muted)] mb-2">Your Referral Code</p>
                    <div className="text-3xl font-bold font-mono text-[var(--primary)] tracking-widest mb-4">
                      {referralCode.code}
                    </div>
                    <div className="flex gap-3 justify-center">
                      <Button
                        onClick={copyReferralCode}
                        className="btn-secondary"
                        data-testid="copy-referral-code"
                      >
                        {copiedCode ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        {copiedCode ? 'Copied!' : 'Copy Code'}
                      </Button>
                      <Button
                        onClick={shareReferralLink}
                        className="btn-primary"
                        data-testid="share-referral"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share Link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={fetchReferralData} className="btn-primary">
                    Get My Referral Code
                  </Button>
                )}
              </div>

              {/* Stats */}
              {referralStats && (
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="card p-6 text-center">
                    <p className="text-3xl font-bold text-[var(--primary)]">{referralStats.total_referrals}</p>
                    <p className="text-sm text-[var(--text-muted)]">Total Referrals</p>
                  </div>
                  <div className="card p-6 text-center">
                    <p className="text-3xl font-bold text-[var(--success)]">{referralStats.completed_referrals}</p>
                    <p className="text-sm text-[var(--text-muted)]">Completed</p>
                  </div>
                  <div className="card p-6 text-center">
                    <p className="text-3xl font-bold text-[#D4AF37]">${referralStats.total_earned.toFixed(2)}</p>
                    <p className="text-sm text-[var(--text-muted)]">Total Earned</p>
                  </div>
                </div>
              )}

              {/* How it works */}
              <div className="card p-6 mb-8">
                <h3 className="font-bold text-lg mb-4">How It Works</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-[var(--primary-light)] flex items-center justify-center mx-auto mb-3">
                      <Share2 className="w-6 h-6 text-[var(--primary)]" />
                    </div>
                    <h4 className="font-semibold mb-1">1. Share</h4>
                    <p className="text-sm text-[var(--text-muted)]">Share your unique code with friends</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-[var(--primary-light)] flex items-center justify-center mx-auto mb-3">
                      <Users className="w-6 h-6 text-[var(--primary)]" />
                    </div>
                    <h4 className="font-semibold mb-1">2. They Book</h4>
                    <p className="text-sm text-[var(--text-muted)]">Friend signs up and makes first booking</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-[var(--primary-light)] flex items-center justify-center mx-auto mb-3">
                      <Gift className="w-6 h-6 text-[var(--primary)]" />
                    </div>
                    <h4 className="font-semibold mb-1">3. You Earn</h4>
                    <p className="text-sm text-[var(--text-muted)]">Both get $10! You earn credits, they save.</p>
                  </div>
                </div>
              </div>

              {/* Referral History */}
              {myReferrals.length > 0 && (
                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">Your Referrals</h3>
                  <div className="space-y-3">
                    {myReferrals.map((referral) => (
                      <div 
                        key={referral.id}
                        className="flex items-center justify-between p-4 bg-[var(--accent)] rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[var(--primary-light)] flex items-center justify-center">
                            <User className="w-5 h-5 text-[var(--primary)]" />
                          </div>
                          <div>
                            <p className="font-medium">{referral.referred_name}</p>
                            <p className="text-sm text-[var(--text-muted)]">
                              {new Date(referral.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`badge-${referral.status === 'credited' ? 'success' : referral.status === 'completed' ? 'primary' : 'warning'}`}>
                          {referral.status === 'credited' ? '+$10 Earned' : referral.status === 'completed' ? 'Pending Credit' : 'Pending Booking'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-6 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-[var(--primary)]" />
                Give Feedback
              </h2>

              {feedbackLoading ? (
                <div className="card p-12 text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-[var(--text-muted)]">Loading...</p>
                </div>
              ) : (
                <>
                  {/* Leave a Review Section */}
                  {bookingsToReview.length > 0 && (
                    <div className="card p-6 mb-8 border-2 border-[var(--primary)]">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-500" />
                        Rate Your Recent Service
                      </h3>
                      <p className="text-[var(--text-muted)] mb-4">
                        You have {bookingsToReview.length} completed service(s) to review
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Select Booking</label>
                          <select
                            value={reviewForm.booking_id}
                            onChange={(e) => setReviewForm({ ...reviewForm, booking_id: e.target.value })}
                            className="input-styled w-full"
                          >
                            <option value="">Choose a service to review...</option>
                            {bookingsToReview.map(booking => (
                              <option key={booking.id} value={booking.id}>
                                {booking.service_name} - {new Date(booking.scheduled_date).toLocaleDateString()}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Your Rating</label>
                          <div className="flex gap-1">
                            {renderStars(reviewForm.rating, true, (rating) => setReviewForm({ ...reviewForm, rating }))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Your Feedback (Optional)</label>
                          <Textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                            placeholder="Tell us about your experience..."
                            rows={4}
                            className="input-styled"
                          />
                        </div>

                        <Button
                          onClick={handleSubmitReview}
                          disabled={!reviewForm.booking_id || submittingReview}
                          className="btn-primary w-full"
                        >
                          {submittingReview ? 'Submitting...' : 'Submit Review'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* My Reviews - Customer Review History */}
                  <div className="card p-6" data-testid="customer-review-history">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                      <History className="w-5 h-5 text-[var(--primary)]" />
                      Your Review History
                    </h3>
                    {myReviews.length === 0 ? (
                      <div className="text-center py-8">
                        <Star className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-3" />
                        <p className="text-[var(--text-muted)]">
                          {bookingsToReview.length > 0 
                            ? "You haven't left any reviews yet. Share your experience above!"
                            : "Complete a service to leave a review"
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {myReviews.map(review => (
                          <div 
                            key={review.id} 
                            className="p-4 bg-[var(--accent)] rounded-xl border border-[var(--border-primary)]"
                            data-testid={`review-card-${review.id}`}
                          >
                            {/* Review Header */}
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-semibold text-lg">{review.service_name || 'Service'}</p>
                                <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                                  <span>{new Date(review.created_at).toLocaleDateString()}</span>
                                  {review.worker_name && (
                                    <>
                                      <span>•</span>
                                      <span>Worker: {review.worker_name}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-1">
                                  {renderStars(review.rating)}
                                </div>
                                {review.is_approved ? (
                                  <span className="badge-success text-xs">Published</span>
                                ) : (
                                  <span className="badge-warning text-xs">Pending Approval</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Your Comment */}
                            {review.comment && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-[var(--text-muted)] mb-1">Your Review:</p>
                                <p className="text-[var(--text-secondary)] italic">&quot;{review.comment}&quot;</p>
                              </div>
                            )}
                            
                            {/* Owner Response */}
                            {review.owner_response && (
                              <div className="mt-3 pt-3 border-t border-[var(--border-primary)]">
                                <div className="bg-[var(--primary)]/10 p-3 rounded-lg border-l-4 border-[var(--primary)]">
                                  <div className="flex items-center gap-2 mb-2">
                                    <MessageSquare className="w-4 h-4 text-[var(--primary)]" />
                                    <span className="text-sm font-semibold text-[var(--primary)]">
                                      Response from NC Help Hub
                                    </span>
                                    {review.owner_response_at && (
                                      <span className="text-xs text-[var(--text-muted)]">
                                        • {new Date(review.owner_response_at).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[var(--text-secondary)] text-sm">
                                    {review.owner_response}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FavoriteWorkersTab />
          </motion.div>
        )}

        {/* Addresses Tab */}
        {activeTab === 'addresses' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <SavedAddressesTab />
          </motion.div>
        )}

        {/* Family Tab */}
        {activeTab === 'family' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FamilyMembersTab />
          </motion.div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <PaymentMethodsTab />
          </motion.div>
        )}

        {/* Uploads Tab */}
        {activeTab === 'uploads' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="customer-uploads-tab">
            <UniversalUploader userRole="customer" />
          </motion.div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <ServiceGalleryTab />
          </motion.div>
        )}

        {/* Custom Requests Tab */}
        {activeTab === 'customrequests' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <CustomRequestsTab />
          </motion.div>
        )}

        {/* Service Requests Tab */}
        {activeTab === 'servicerequests' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <ServiceRequestsTab />
          </motion.div>
        )}

        {/* Rides Tab */}
        {activeTab === 'rides' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Quick book link for mobile */}
            <div className="md:hidden mb-4">
              <Link to="/book-ride">
                <Button className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white py-4 text-lg font-semibold rounded-xl shadow-lg">
                  <Car className="w-5 h-5 mr-2" />
                  Book a New Ride
                  <ChevronRight className="w-5 h-5 ml-auto" />
                </Button>
              </Link>
            </div>
            <RideBookingTab />
          </motion.div>
        )}

        {activeTab === 'incidents' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <IncidentReportSection userRole="customer" />
          </motion.div>
        )}

        {activeTab === 'documents' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <CustomerPoliciesTab />
          </motion.div>
        )}

        {/* Home Tab - Mobile Dashboard Overview */}
        {activeTab === 'home' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Welcome Section */}
            <div className="mb-4">
              <h1 className="text-2xl font-bold font-['Cormorant_Garamond']">
                Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
              </h1>
              <p className="text-[var(--text-muted)] text-sm mt-1">
                What can we help you with today?
              </p>
            </div>

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setActiveTab('services')}
                className="flex flex-col items-center justify-center p-4 rounded-2xl bg-[var(--background-paper)] border border-[var(--border-primary)] hover:border-[var(--primary)] transition-all active:scale-95 min-h-[80px]"
              >
                <Sparkles className="w-6 h-6 text-[var(--primary)] mb-1" />
                <span className="text-xs font-medium">Book Now</span>
              </button>
              <button
                onClick={() => setActiveTab('rides')}
                className="relative flex flex-col items-center justify-center p-4 rounded-2xl bg-[var(--background-paper)] border border-[var(--border-primary)] hover:border-[var(--primary)] transition-all active:scale-95 min-h-[80px]"
              >
                <Car className="w-6 h-6 text-[var(--primary)] mb-1" />
                <span className="text-xs font-medium">Book Ride</span>
                {activeRides.length > 0 && (
                  <span className="absolute top-2 right-2 w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                    {activeRides.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('bookings')}
                className="relative flex flex-col items-center justify-center p-4 rounded-2xl bg-[var(--background-paper)] border border-[var(--border-primary)] hover:border-[var(--primary)] transition-all active:scale-95 min-h-[80px]"
              >
                <Calendar className="w-6 h-6 text-[var(--primary)] mb-1" />
                <span className="text-xs font-medium">Bookings</span>
                {bookings.filter(b => b.status === 'pending').length > 0 && (
                  <span className="absolute top-2 right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {bookings.filter(b => b.status === 'pending').length}
                  </span>
                )}
              </button>
            </div>

            {/* Active Rides Progress Tracker */}
            {activeRides.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Car className="w-5 h-5 text-blue-500" />
                    Active Rides
                  </h2>
                  <button onClick={() => setActiveTab('rides')} className="text-sm text-[var(--primary)]">View All</button>
                </div>
                
                {activeRides.map((ride) => {
                  const statusConfig = {
                    requested: { label: 'Finding Driver', color: 'bg-yellow-500', icon: Clock, progress: 20 },
                    driver_assigned: { label: 'Driver Assigned', color: 'bg-blue-500', icon: User, progress: 40 },
                    en_route_pickup: { label: 'Driver En Route', color: 'bg-purple-500', icon: Navigation, progress: 60 },
                    arrived_pickup: { label: 'Driver Arrived', color: 'bg-cyan-500', icon: MapPin, progress: 80 },
                    in_progress: { label: 'In Transit', color: 'bg-green-500', icon: Car, progress: 90 }
                  };
                  const config = statusConfig[ride.status] || { label: ride.status, color: 'bg-gray-500', icon: Clock, progress: 0 };
                  const StatusIcon = config.icon;
                  
                  return (
                    <div 
                      key={ride.id} 
                      className="card p-4 cursor-pointer hover:border-[var(--primary)] transition-all"
                      onClick={() => {
                        setSelectedRideForTracking(ride);
                        setActiveTab('rides');
                      }}
                      data-testid={`active-ride-${ride.id}`}
                    >
                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-[var(--primary)]">{ride.reference_number || ride.reference}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full text-white ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <div className="h-2 bg-[var(--background-subtle)] rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${config.color} transition-all duration-500`}
                            style={{ width: `${config.progress}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Route */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500 mt-1 shrink-0"></div>
                          <span className="truncate">{ride.pickup_location?.address}, {ride.pickup_location?.city}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500 mt-1 shrink-0"></div>
                          <span className="truncate">{ride.dropoff_location?.address}, {ride.dropoff_location?.city}</span>
                        </div>
                      </div>
                      
                      {/* Driver Info */}
                      {ride.driver_name && (
                        <div className="mt-3 p-3 bg-[var(--background-subtle)] rounded-lg border border-[var(--border-primary)]">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                              {ride.driver_name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold">{ride.driver_name}</p>
                              <p className="text-xs text-[var(--text-muted)]">Your Driver</p>
                            </div>
                            <div className="text-right">
                              {ride.driver_vehicle && (
                                <p className="text-xs text-[var(--text-muted)]">{ride.driver_vehicle}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Contact Buttons */}
                          <div className="flex gap-2">
                            {ride.driver_phone && (
                              <a 
                                href={`tel:${ride.driver_phone}`}
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  logContactAttempt(ride.id, 'call', 'customer_to_driver');
                                }}
                                data-testid={`customer-call-driver-${ride.id}`}
                              >
                                <Phone className="w-4 h-4" />
                                Call
                              </a>
                            )}
                            {ride.driver_phone && (
                              <a 
                                href={`sms:${ride.driver_phone}`}
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  logContactAttempt(ride.id, 'text', 'customer_to_driver');
                                }}
                                data-testid={`customer-text-driver-${ride.id}`}
                              >
                                <MessageCircle className="w-4 h-4" />
                                Text
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Pickup Time & Fare */}
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className="text-[var(--text-muted)]">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {new Date(ride.pickup_datetime).toLocaleString([], { 
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                          })}
                        </span>
                        <span className="font-semibold text-[var(--primary)]">
                          ${ride.estimated_fare?.toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="mt-3 flex gap-2">
                        <Button 
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRideForTracking(ride);
                            setActiveTab('rides');
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Track Ride
                        </Button>
                        
                        {/* Cancel Button - only for pending/requested rides */}
                        {['requested', 'driver_assigned'].includes(ride.status) && (
                          <Button 
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRide(ride);
                            }}
                            data-testid={`cancel-ride-${ride.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upcoming Appointment */}
            <UpcomingAppointmentCard onMessageWorker={openBookingChat} />

            {/* Quick Pay Alert */}
            {bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length > 0 && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-[var(--warning)]/30 rounded-xl shadow-sm">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-full">
                      <CreditCard className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800">
                        {bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length} pending payment{bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length !== 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-amber-600">
                        Total: ${bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').reduce((sum, b) => sum + (b.total_amount || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setActiveTab('payments')}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    View Payments
                  </Button>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {bookings.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold">Recent Activity</h2>
                  <button onClick={() => setActiveTab('bookings')} className="text-sm text-[var(--primary)]">View All</button>
                </div>
                <div className="space-y-3">
                  {[...bookings]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice(0, 3)
                    .map((booking) => (
                      <div key={booking.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--background-paper)] border border-[var(--border-primary)]">
                        <div className="w-10 h-10 rounded-full bg-[var(--background-subtle)] flex items-center justify-center">
                          {booking.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : booking.status === 'cancelled' ? (
                            <X className="w-5 h-5 text-red-500" />
                          ) : (
                            <Calendar className="w-5 h-5 text-[var(--primary)]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{booking.service_name}</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {new Date(booking.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${
                          booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          booking.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {booking.status?.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* CTA Banner */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] p-5">
              <div className="relative z-10">
                <h3 className="text-black font-bold text-lg mb-1">Need help today?</h3>
                <p className="text-black/70 text-sm mb-4">Book a service in minutes</p>
                <Button onClick={() => setActiveTab('services')} className="bg-black text-white hover:bg-black/80">
                  Browse Services
                </Button>
              </div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
          </motion.div>
        )}

        {/* Profile Tab - Customer Profile & Settings */}
        {activeTab === 'profile' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Profile Header */}
            <div className="card p-6">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-3xl font-bold text-black">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'C'}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold font-['Cormorant_Garamond']">{user?.full_name || 'Customer'}</h2>
                  <p className="text-[var(--text-muted)] text-sm">{user?.email}</p>
                  <p className="text-[var(--text-muted)] text-sm">{user?.phone || 'No phone added'}</p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold mb-3">Account</h3>
              {[
                { id: 'payments', label: 'Payment Methods', icon: CreditCard },
                { id: 'favorites', label: 'Favorite Workers', icon: Heart },
                { id: 'documents', label: 'Signed Documents', icon: FileSignature },
                { id: 'uploads', label: 'My Uploads', icon: Upload },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[var(--background-subtle)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-[var(--primary)]" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
                </button>
              ))}
            </div>

            {/* Rewards & Referrals */}
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold mb-3">Rewards & Referrals</h3>
              {[
                { id: 'loyalty', label: 'Rewards Program', icon: Gift },
                { id: 'referrals', label: 'Refer Friends', icon: Share2 },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[var(--background-subtle)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-[var(--primary)]" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
                </button>
              ))}
            </div>

            {/* Support */}
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold mb-3">Support</h3>
              {[
                { id: 'feedback', label: 'Leave Feedback', icon: Star },
                { id: 'incidents', label: 'Report an Issue', icon: AlertTriangle },
                { id: 'messages', label: 'Contact Support', icon: MessageCircle },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[var(--background-subtle)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-[var(--primary)]" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
                </button>
              ))}
            </div>

            {/* Logout Button */}
            <Button
              onClick={logout}
              variant="destructive"
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </motion.div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <h2 className="text-xl font-bold font-['Cormorant_Garamond']">Notifications</h2>
            <div className="space-y-3">
              {/* Placeholder for notifications - would connect to real notifications API */}
              <div className="text-center py-12 text-[var(--text-muted)]">
                <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No new notifications</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && reviewBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-8 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold font-['Cormorant_Garamond']">Leave a Review</h2>
              <button 
                onClick={() => setShowReviewModal(false)}
                className="p-2 hover:bg-[var(--accent)] rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-[var(--text-muted)] mb-1">Service</p>
              <p className="font-semibold">{reviewBooking.service_name}</p>
              {reviewBooking.worker_name && (
                <>
                  <p className="text-sm text-[var(--text-muted)] mb-1 mt-3">Worker</p>
                  <p className="font-semibold">{reviewBooking.worker_name}</p>
                </>
              )}
            </div>

            <div className="mb-6">
              <p className="text-sm text-[var(--text-muted)] mb-3">How was your experience?</p>
              <div className="flex justify-center">
                <StarRating rating={reviewRating} setRating={setReviewRating} />
              </div>
              <p className="text-center text-sm text-[var(--text-secondary)] mt-2">
                {reviewRating === 1 && 'Poor'}
                {reviewRating === 2 && 'Fair'}
                {reviewRating === 3 && 'Good'}
                {reviewRating === 4 && 'Very Good'}
                {reviewRating === 5 && 'Excellent!'}
              </p>
            </div>

            <div className="mb-6">
              <label className="text-sm text-[var(--text-muted)] mb-2 block">
                Comments (optional)
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Share your experience..."
                className="input-styled w-full h-24 resize-none"
                data-testid="review-comment"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowReviewModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={submitReview}
                disabled={submittingReview}
                className="btn-primary flex-1"
                data-testid="submit-review-btn"
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Chat Modal */}
      {showChat && (
        <ChatModal 
          onClose={() => { setShowChat(false); fetchData(); }} 
          type={chatType}
          bookingId={chatBookingId}
        />
      )}

      {/* Quick Rebook Modal */}
      {showRebookModal && rebookingBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-8 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-[var(--primary)]" />
                Quick Rebook
              </h2>
              <button 
                onClick={() => setShowRebookModal(false)}
                className="p-2 hover:bg-[var(--accent)] rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-[var(--accent)] rounded-xl">
              <p className="text-sm text-[var(--text-muted)] mb-1">Service</p>
              <p className="font-bold">{rebookingBooking.service_name}</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                {rebookingBooking.address}, {rebookingBooking.city}
              </p>
              <p className="text-lg font-bold text-[var(--primary)] mt-2">
                ${rebookingBooking.base_amount + (rebookingBooking.mileage_fee || 0) + (rebookingBooking.addons_total || 0)}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">New Date *</label>
                <Input
                  type="date"
                  value={rebookDate}
                  onChange={(e) => setRebookDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="input-styled"
                  required
                  data-testid="rebook-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Time *</label>
                <Input
                  type="time"
                  value={rebookTime}
                  onChange={(e) => setRebookTime(e.target.value)}
                  className="input-styled"
                  required
                  data-testid="rebook-time"
                />
              </div>
              {rebookingBooking.worker_name && (
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="same_worker"
                    checked={rebookSameWorker}
                    onChange={(e) => setRebookSameWorker(e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--border-primary)]"
                  />
                  <label htmlFor="same_worker" className="text-sm">
                    Request same worker ({rebookingBooking.worker_name})
                  </label>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowRebookModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleQuickRebook}
                disabled={rebooking || !rebookDate || !rebookTime}
                className="btn-primary flex-1"
                data-testid="confirm-rebook-btn"
              >
                {rebooking ? 'Creating...' : 'Rebook Now'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tip Worker Modal */}
      {showTipModal && tipBooking && (
        <TipWorker 
          booking={tipBooking}
          onClose={() => { setShowTipModal(false); setTipBooking(null); }}
          onSuccess={handleTipSuccess}
        />
      )}

      {/* Cancel Booking Modal */}
      {showCancelModal && cancellingBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl max-w-md w-full p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">Cancel Booking</h3>
              <button onClick={() => { setShowCancelModal(false); setCancellingBooking(null); }} className="text-gray-400 hover:text-[var(--text-secondary)]">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Cancellation Policy:</strong> Bookings must be cancelled at least 24 hours in advance.
              </p>
            </div>

            <div className="mb-4">
              <p className="text-sm text-[var(--text-secondary)]">
                <strong>Service:</strong> {cancellingBooking.service_name}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                <strong>Date:</strong> {cancellingBooking.scheduled_date} at {cancellingBooking.scheduled_time}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Reason for cancellation (optional)</label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Let us know why you&apos;re cancelling..."
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowCancelModal(false); setCancellingBooking(null); setCancelReason(''); }}
              >
                Keep Booking
              </Button>
              <Button
                className="flex-1 bg-[var(--error)] hover:bg-[var(--error)]/80 text-white"
                onClick={handleCancelBooking}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Support Button */}
      <SupportButton 
        currentUser={user}
        onStartChat={handleStartChat}
        onStartCall={handleStartCall}
      />

      {/* Video/Voice Call Modal */}
      {showCallModal && activeCall && (
        <VideoCallModal
          isOpen={showCallModal}
          onClose={() => {
            setActiveCall(null);
            setShowCallModal(false);
          }}
          callData={activeCall}
          currentUser={user}
          onCallEnd={() => {
            setActiveCall(null);
            setShowCallModal(false);
          }}
        />
      )}

      {/* Incoming Call Modal */}
      {incomingCall && (
        <IncomingCallModal
          isOpen={!!incomingCall}
          caller={incomingCall?.otherParty}
          callType={incomingCall?.callType}
          onAccept={() => {
            setActiveCall({ ...incomingCall, direction: 'incoming' });
            setIncomingCall(null);
            setShowCallModal(true);
          }}
          onDecline={() => {
            setIncomingCall(null);
          }}
        />
      )}

      {/* Mobile Booking Wizard */}
      {showMobileBooking && selectedServiceForMobile && (
        <MobileBookingWizard
          service={selectedServiceForMobile}
          onComplete={async (bookingData) => {
            try {
              const response = await bookingsAPI.create({
                service_id: bookingData.serviceId,
                service_type: bookingData.serviceType,
                scheduled_date: bookingData.scheduledDate,
                scheduled_time: bookingData.scheduledTime,
                address: bookingData.address,
                apartment: bookingData.apartment,
                city: bookingData.city,
                state: bookingData.state,
                zip_code: bookingData.zipCode,
                client_name: bookingData.clientName || user?.full_name,
                client_email: bookingData.clientEmail || user?.email,
                client_phone: bookingData.clientPhone,
                special_requests: bookingData.specialRequests,
                urgency: bookingData.urgency,
                payment_method: bookingData.paymentMethod,
                selected_addons: bookingData.selectedAddons
              });
              toast.success('Booking created successfully!');
              setShowMobileBooking(false);
              setSelectedServiceForMobile(null);
              // Refresh bookings data
              fetchData();
              // Navigate to payment if card payment
              if (bookingData.paymentMethod === 'card' && response.data?.id) {
                navigate(`/customer/pay/${response.data.id}`);
              }
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to create booking');
              throw error;
            }
          }}
          onCancel={() => {
            setShowMobileBooking(false);
            setSelectedServiceForMobile(null);
          }}
          initialData={{
            clientName: user?.full_name,
            clientEmail: user?.email,
            clientPhone: user?.phone
          }}
        />
      )}

      {/* Mobile Bottom Navigation - Customer: Home | Book | Bookings | Messages | Profile */}
      <BottomNavBar
        tabs={[
          { id: 'home', label: 'Home', icon: 'Home' },
          { id: 'services', label: 'Book', icon: 'Sparkles' },
          { id: 'bookings', label: 'Bookings', icon: 'Calendar' },
          { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        moreMenuItems={[
          { id: 'rides', label: 'My Rides', icon: 'Car' },
          { id: 'profile', label: 'Profile', icon: 'User' },
          { id: 'payments', label: 'Payments', icon: 'CreditCard' },
          { id: 'customrequests', label: 'Request', icon: 'ClipboardList' },
          { id: 'feedback', label: 'Feedback', icon: 'Star' },
          { id: 'incidents', label: 'Report Issue', icon: 'AlertTriangle' },
          { id: 'loyalty', label: 'Rewards', icon: 'Award' },
          { id: 'favorites', label: 'Favorites', icon: 'Heart' },
          { id: 'documents', label: 'Documents', icon: 'FileText' },
          { id: 'uploads', label: 'Uploads', icon: 'Upload' },
          { id: 'notifications', label: 'Notifications', icon: 'Bell' },
        ]}
        badge={{ messages: unreadMessages }}
      />

      {/* Cancel Ride Confirmation Modal */}
      {showRideCancelModal && rideToCancel && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card max-w-sm w-full p-6 space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Cancel Ride?</h3>
              <p className="text-[var(--text-muted)]">
                Are you sure you want to cancel this ride?
              </p>
            </div>
            
            {/* Ride Summary */}
            <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Reference:</span>
                <span className="font-mono">{rideToCancel.reference_number || rideToCancel.reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">From:</span>
                <span className="truncate ml-2">{rideToCancel.pickup_location?.city}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">To:</span>
                <span className="truncate ml-2">{rideToCancel.dropoff_location?.city}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Fare:</span>
                <span className="font-semibold text-[var(--primary)]">${rideToCancel.estimated_fare?.toFixed(2)}</span>
              </div>
            </div>
            
            {rideToCancel.driver_name && (
              <p className="text-sm text-amber-500 text-center">
                ⚠️ A driver has already been assigned. Please only cancel if necessary.
              </p>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowRideCancelModal(false);
                  setRideToCancel(null);
                }}
              >
                Keep Ride
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={confirmCancelRide}
                disabled={cancellingRideId === rideToCancel.id}
              >
                {cancellingRideId === rideToCancel.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Cancel Ride
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
